/**
 * Campaign service — centralises all campaign-related API calls.
 *
 * Covers: campaign CRUD, storefront configuration, shopper management,
 * lock/complete workflow, campaign product categories, price changes,
 * order retrieval, and campaign validation helpers.
 */
import api from './api'

// ---------------------------------------------------------------------------
// Campaign CRUD & Retrieval
// ---------------------------------------------------------------------------

export function getCampaignById(campaignId) {
  return api.get(`/campaign/getCampaignById/${campaignId}`)
}

export function getCampaignList() {
  return api.get('/campaign/getCampaignList')
}

export function getAccountCampaignList() {
  return api.get('/campaign/getAccountCampaignList')
}

export function getReportCampaignList() {
  return api.get('/campaign/getReportCampaignList')
}

export function getCampaignCount() {
  return api.get('/campaign/getCampaignCount')
}

export function getAllCampaignIDList() {
  return api.get('/campaign/getAllCampaignIDList')
}

export function deleteCampaign(campaignId) {
  return api.get(`/campaign/deleteCampaign/${campaignId}`)
}

export function updateCampaign(campaignId, args) {
  return api.post(`/stores/${campaignId}/update_campaign`, {
    meta: { method: 'update_campaign', args },
  })
}

// ---------------------------------------------------------------------------
// Storefront & Shopper
// ---------------------------------------------------------------------------

export function getStorefrontCampaign(campaignId) {
  return api.get(`/campaign/getStorefrontCampaign/${campaignId}`)
}

export function getCampaignSignInInfo(campaignId) {
  return api.post('/campaign/getCampaignSignInInfo', { campaign_id: campaignId })
}

export function validateStorePassword(campaignId, password) {
  return api.post('/campaign/validateStorePassword', { campaign_id: campaignId, password })
}

export function updateShopper(data) {
  return api.post('/campaign/updateShopper', data)
}

// ---------------------------------------------------------------------------
// Campaign Status & Activation
// ---------------------------------------------------------------------------

export function getIsCampaignActive(campaignId) {
  return api.get(`/campaign/getIsCampaignActive/${campaignId}`)
}

export function activeCampaign(campaignId) {
  return api.get(`/campaign/activeCampaign/${campaignId}`)
}

export function setLockStatusCampaign(campaignId, complete, distributorPo, endDate) {
  return api.get(
    `/campaign/setLockStatusCampaign/${campaignId}/${complete}/${distributorPo}/${endDate}`
  )
}

export function sendEmailAfterLockCampaign(campaignId) {
  return api.get(`/campaign/sendEmailAfterLockCampaign/${campaignId}`)
}

export function updateCampaignEndDateWithId(campaignId, endDate) {
  return api.get(`/campaign/updateCampaignEndDateWithId/${campaignId}/${endDate}`)
}

export function getUserHasAccessToCampaign(campaignId) {
  return api.get(`/campaign/getUserHasAccessToCampaign/${campaignId}`)
}

// ---------------------------------------------------------------------------
// Campaign Overview & Orders
// ---------------------------------------------------------------------------

export function getCampaignOrderOverviewWithId(campaignId) {
  return api.get(`/campaign/getCampaignOrderOverviewWithId/${campaignId}`)
}

export function getOrders(data) {
  return api.post('/campaign/getOrders', data)
}

// ---------------------------------------------------------------------------
// Campaign Stock & Pricing
// ---------------------------------------------------------------------------

export function getCampaignStockInfo(data) {
  return api.post('/campaign/getCampaignStockInfo/', data)
}

export function updateCampaignBFLCost(campaignId, bflCost) {
  return api.get(`/campaign/updateCampaignBFLCost/${campaignId}/${bflCost}`)
}

export function updateCampaignProductsWithBFLCost(campaignId, bflCost) {
  return api.get(`/campaign/updateCampaignProductsWithBFLCost/${campaignId}/${bflCost}`)
}

// ---------------------------------------------------------------------------
// Product Categories & Decorations
// ---------------------------------------------------------------------------

// D2 (follow-up, card https://trello.com/c/a7j1VV6D): addProductCategory
// (POST /campaign/addProductCategory) was removed here -- it appended
// free-text strings onto the legacy stores.product_categories flat list.
// Category authoring now lives exclusively on the store_categories
// entity via the D1-migrated StorefrontListingPanel.

export function getUsedCustomDropdownOptions(campaignId) {
  return api.get(`/campaign/getUsedCustomDropdownOptions/${campaignId}`)
}

export function getUsedCouponCodeList(campaignId) {
  return api.get(`/campaign/getUsedCouponCodeList/${campaignId}`)
}

// ---------------------------------------------------------------------------
// Attributes
// ---------------------------------------------------------------------------

export function getOrderedPrimaryAttributes(productId, campaignId) {
  return api.get(`/campaign/getOrderedPrimaryAttributes/${productId}/${campaignId}`)
}

export function getOrderedSecondaryAttributesList(productId, campaignId) {
  return api.get(`/campaign/getOrderedSecondaryAttributesList/${productId}/${campaignId}`)
}

export function getOrderedSecondaryAttributes(productId, campaignId) {
  return api.get(`/campaign/getOrderedSecondaryAttributes/${productId}/${campaignId}`)
}

export function getIsProductsIncomplete(campaignId) {
  return api.get(`/campaign/getIsProductsIncomplete/${campaignId}`)
}

export function propagateOrderItem(data) {
  return api.post('/campaign/propagateOrderItem', data)
}

// ---------------------------------------------------------------------------
// Misc Campaign Helpers
// ---------------------------------------------------------------------------

export function getDistributorsList() {
  return api.get('/campaign/getDistributorsList')
}

export function getCountryCodes() {
  return api.get('/campaign/getCountryCodes')
}

export function getBrandColorOptions() {
  return api.get('/campaign/getBrandColorOptions')
}

// ---------------------------------------------------------------------------
// SAFRS / JSON:API model endpoints
// ---------------------------------------------------------------------------

export function patchCampaignRelationship(campaignId, relationship, data) {
  return api.patch(`/stores/${campaignId}/${relationship}`, { data })
}
